"use client";

import { useContext, useState, useMemo } from "react";
import Link from "next/link";
import { useSelectedLayoutSegment } from "next/navigation";
import { signOut, useSession } from "next-auth/react";
import { Menu, ShoppingCart, ShieldCheck, Package, Sparkles, BookOpen } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useStoreState } from "easy-peasy";

import { marketingConfig } from "@/config/marketing";
import { siteConfig } from "@/config/site";
import { cn } from "@/lib/utils";
import { useScroll } from "@/hooks/use-scroll";
import { useIsLocalhost } from "@/hooks/use-is-localhost";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ModalContext } from "@/components/modals/providers";
import { Icons } from "@/components/shared/icons";
import { UserAvatar } from "@/components/shared/user-avatar";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { UserAccountNav } from "@/components/layout/user-account-nav";
import { ModeToggle } from "@/components/layout/mode-toggle";
import { BalanceWidget } from "@/components/layout/balance-widget";
import { LeftNavDrawer, RightAccountDrawer } from "@/components/layout/mobile-drawers";
import type { MobileNavItem } from "@/components/layout/mobile-drawers";
import type { StoreModel } from "@/store";

interface NavBarProps {
  scroll?: boolean;
  large?: boolean;
}

export function NavBar({ scroll = false }: NavBarProps) {
  const scrolled = useScroll(50);
  const isLocalhost = useIsLocalhost();
  const { data: session, status } = useSession();
  const { setShowSignInModal } = useContext(ModalContext);

  const selectedLayout = useSelectedLayoutSegment();

  // Mobile drawer states
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
  const [isAccountDrawerOpen, setIsAccountDrawerOpen] = useState(false);

  const rawLinks = marketingConfig.mainNav;
  const links = useMemo(() => {
    if (!rawLinks) return [];
    if (isLocalhost) return rawLinks;
    return rawLinks.filter(
      (item) =>
        item.href !== "/docs" &&
        !item.href.startsWith("/docs/") &&
        item.href !== "/guides" &&
        !item.href.startsWith("/guides/") &&
        item.title.toLowerCase() !== "guides"
    );
  }, [rawLinks, isLocalhost]);
  const isAdmin = session?.user?.role === "ADMIN";

  const mobileNavItems = useMemo((): MobileNavItem[] => {
    const base: MobileNavItem[] = (links ?? []).map((item) => {
      let icon = item.icon ? Icons[item.icon] : undefined;
      let description: string | undefined;

      if (item.title.toLowerCase() === "products") {
        icon = Sparkles;
        description = "DMA Cheats & Firmware";
      } else if (item.title.toLowerCase() === "guides") {
        icon = BookOpen;
        description = "Setup & installation guides";
      }

      return {
        label: item.title,
        link: item.disabled ? "#" : item.href,
        icon,
        description,
        external: item.external,
        isActive:
          item.href.startsWith(`/${selectedLayout}`) ||
          (item.href === "/docs" && selectedLayout === "guides"),
      };
    });

    return base;
  }, [links, selectedLayout]);

  return (
    <>
      <header
        className={`sticky top-0 z-40 flex w-full justify-center bg-background/60 backdrop-blur-xl transition-all ${scroll ? (scrolled ? "border-b" : "bg-transparent") : "bg-transparent"
          }`}
      >
        <MaxWidthWrapper className="relative flex h-14 items-center justify-between py-4">
          {/* ========================================================= */}
          {/* 1. LEFT COLUMN                                            */}
          {/* - Mobile: Hamburger menu button (opens Left Drawer)        */}
          {/* - Desktop: Logo + Main Navigation links                   */}
          {/* ========================================================= */}
          <div className="flex items-center gap-6 md:gap-10">
            {/* Mobile Hamburger Trigger (opens drawer from left) */}
            <button
              type="button"
              onClick={() => setIsNavDrawerOpen(true)}
              className="flex size-9 items-center justify-center rounded-lg border border-border/50 bg-muted/40 text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring transition-colors md:hidden cursor-pointer"
              aria-label="Open navigation menu"
            >
              <Menu className="size-4" strokeWidth={2} />
            </button>

            {/* Desktop Logo */}
            <Link href="/" className="hidden md:flex items-center space-x-1.5">
              <Icons.logo className="size-6" />
              <span className="font-urban text-xl font-bold tracking-tight text-foreground">
                {siteConfig.name}
              </span>
            </Link>

            {/* Desktop Navigation Links */}
            {links && links.length > 0 ? (
              <nav className="hidden md:flex gap-6 items-center">
                {links.map((item, index) => {
                  const Icon = item.icon ? Icons[item.icon] : null;
                  return (
                    <Link
                      key={index}
                      href={item.disabled ? "#" : item.href}
                      prefetch={!item.external}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noreferrer" : undefined}
                      className={cn(
                        "flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-foreground/80",
                        item.href.startsWith(`/${selectedLayout}`) ||
                          (item.href === "/docs" && selectedLayout === "guides")
                          ? "text-foreground font-semibold"
                          : "text-foreground/60",
                        item.disabled && "cursor-not-allowed opacity-80"
                      )}
                    >
                      {Icon && <Icon className="size-4" />}
                      <span>{item.title}</span>
                    </Link>
                  );
                })}

                {/* Admin Nav Link */}
                {isAdmin && (
                  <Link
                    href="/admin"
                    className={cn(
                      "flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-extrabold text-primary transition-all hover:bg-primary hover:text-primary-foreground",
                      selectedLayout === "admin" && "bg-primary text-primary-foreground"
                    )}
                  >
                    <ShieldCheck className="size-3.5" />
                    <span>Admin</span>
                  </Link>
                )}
              </nav>
            ) : null}
          </div>

          {/* ========================================================= */}
          {/* 2. CENTER COLUMN (Steam-style Mobile Centered Logo)       */}
          {/* ========================================================= */}
          <div className="flex md:hidden absolute left-1/2 -translate-x-1/2 items-center pointer-events-auto">
            <Link href="/" className="flex items-center space-x-1.5 outline-none">
              <Icons.logo className="size-6" />
              <span className="font-urban text-lg font-bold tracking-tight text-foreground">
                {siteConfig.name}
              </span>
            </Link>
          </div>

          {/* ========================================================= */}
          {/* 3. RIGHT COLUMN                                           */}
          {/* - Mobile: Avatar button (opens Right Account Drawer)      */}
          {/* - Desktop: Balance + Cart + Theme Switcher + User Nav     */}
          {/* ========================================================= */}
          <div className="flex items-center gap-2 sm:gap-2.5">
            {/* Desktop: Balance Widget */}
            <div className="hidden md:flex">
              {session?.user ? <BalanceWidget /> : null}
            </div>

            {/* Desktop: Cart Icon */}
            <div className="hidden md:flex">
              <CartIcon />
            </div>

            {/* Desktop: Mode Toggle */}
            <div className="hidden md:flex">
              <ModeToggle />
            </div>

            {/* Desktop: User Avatar / Sign In */}
            <div className="hidden md:flex">
              {status === "loading" ? (
                <Skeleton className="size-9 rounded-full" />
              ) : session?.user ? (
                <UserAccountNav />
              ) : (
                <Button
                  className="gap-2 px-4"
                  variant="default"
                  size="sm"
                  rounded="full"
                  onClick={() => setShowSignInModal(true)}
                >
                  <span>Sign In</span>
                  <Icons.arrowRight className="size-4" />
                </Button>
              )}
            </div>

            {/* Mobile: Avatar / Account Trigger (opens account drawer if logged in, opens sign-in modal if guest) */}
            <button
              type="button"
              onClick={() => {
                if (session?.user) {
                  setIsAccountDrawerOpen(true);
                } else {
                  setShowSignInModal(true);
                }
              }}
              className="flex md:hidden size-9 items-center justify-center rounded-full border border-border/60 bg-muted/40 hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-ring transition-all cursor-pointer overflow-hidden shadow-2xs"
              aria-label={session?.user ? "Open account menu" : "Sign in"}
            >
              {status === "loading" ? (
                <Skeleton className="size-8 rounded-full" />
              ) : session?.user ? (
                <UserAvatar
                  user={{
                    name: session.user.name || null,
                    image: session.user.image || null,
                  }}
                  className="size-8"
                />
              ) : (
                <Icons.user className="size-4 text-muted-foreground" />
              )}
            </button>
          </div>
        </MaxWidthWrapper>
      </header>

      {/* ========================================================= */}
      {/* 4. MOBILE DRAWERS                                         */}
      {/* ========================================================= */}
      <LeftNavDrawer
        isOpen={isNavDrawerOpen}
        onClose={() => setIsNavDrawerOpen(false)}
        items={mobileNavItems}
      />

      {session?.user ? (
        <RightAccountDrawer
          isOpen={isAccountDrawerOpen}
          onClose={() => setIsAccountDrawerOpen(false)}
          user={session.user}
          onSignOut={() => signOut({ callbackUrl: "/" })}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  CartIcon — shows a ShoppingCart with animated badge count          */
/* ------------------------------------------------------------------ */

function CartIcon() {
  const items = useStoreState((state: StoreModel) => state.cart.items);
  const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Link
      href="/checkout"
      aria-label={`Cart${totalQty > 0 ? ` — ${totalQty} item${totalQty !== 1 ? "s" : ""}` : ""}`}
      className="relative flex size-9 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ShoppingCart className="size-5" strokeWidth={1.8} />

      <AnimatePresence>
        {totalQty > 0 && (
          <motion.span
            key="cart-badge"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 28 }}
            className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-600 px-[3px] text-[10px] font-bold leading-none text-white shadow"
          >
            {totalQty > 99 ? "99+" : totalQty}
          </motion.span>
        )}
      </AnimatePresence>
    </Link>
  );
}
